// The cart is the client's; the order is the server's. One cart per store,
// 30-minute idle expiry (v3 session rules, carried deliberately), persisted
// in localStorage. Each line carries the ready-to-send server CartLine
// (models/orders.rs) plus display fields; totals shown mid-flow are display
// math — checkout re-prices everything through POST orders/quote.

import { useSyncExternalStore } from 'react';
import type { Channel, WireLine } from './api';

export type CartLine = {
  lineId: string;
  name: string;
  itemType: string;
  /** unit price in cents for the current channel, options included (display) */
  unitPriceInCents: number;
  /** human summary of the configuration ("Large · Gluten Free · + Pepperoni · no Cheese") */
  configSummary: string;
  /** the server payload — quantity lives here */
  wire: WireLine;
};

export type Cart = {
  storeKey: string; // `${rid}/${sid}`
  channel: Channel;
  lines: CartLine[];
  updatedAt: number;
};

const KEY = 'rp-order-cart';
const EXPIRY_MS = 30 * 60_000;

let listeners: Array<() => void> = [];
let cache: Cart | null = null;
let cacheRaw: string | null = null;

function read(storeKey: string): Cart | null {
  const raw = localStorage.getItem(KEY);
  if (raw === cacheRaw && cache && cache.storeKey === storeKey) return cache;
  if (!raw) return null;
  try {
    const c = JSON.parse(raw) as Cart;
    if (c.storeKey !== storeKey) return null; // one cart per store
    if (Date.now() - c.updatedAt > EXPIRY_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    if (c.lines.some((l) => !l.wire || !l.wire.itemType)) return null; // carts from an older contract drop
    cache = c;
    cacheRaw = raw;
    return c;
  } catch {
    return null;
  }
}

function write(cart: Cart | null): void {
  if (cart) {
    cart.updatedAt = Date.now();
    cacheRaw = JSON.stringify(cart);
    // useSyncExternalStore only re-renders when the snapshot identity
    // changes, so the cached snapshot must be a fresh object — mutating
    // the one prior renders hold would leave the UI stale.
    cache = JSON.parse(cacheRaw) as Cart;
    localStorage.setItem(KEY, cacheRaw);
  } else {
    cache = null;
    cacheRaw = null;
    localStorage.removeItem(KEY);
  }
  listeners.forEach((l) => l());
}

export function useCart(storeKey: string): Cart | null {
  return useSyncExternalStore(
    (cb) => {
      listeners.push(cb);
      return () => {
        listeners = listeners.filter((l) => l !== cb);
      };
    },
    () => read(storeKey),
  );
}

export function cartFor(storeKey: string, channel: Channel): Cart {
  return read(storeKey) ?? { storeKey, channel, lines: [], updatedAt: Date.now() };
}

/** Add a configured line; identical configurations merge into quantity. */
export function addLine(storeKey: string, channel: Channel, line: Omit<CartLine, 'lineId'>): void {
  const cart = cartFor(storeKey, channel);
  const sig = (w: WireLine) => JSON.stringify({ ...w, quantity: 0 });
  const twin = cart.lines.find((l) => sig(l.wire) === sig(line.wire));
  if (twin) {
    twin.wire.quantity += line.wire.quantity;
  } else {
    cart.lines.push({ ...line, lineId: `ln_${Math.random().toString(36).slice(2, 10)}` });
  }
  write(cart);
}

export function setQuantity(storeKey: string, lineId: string, quantity: number): void {
  const cart = read(storeKey);
  if (!cart) return;
  if (quantity <= 0) {
    cart.lines = cart.lines.filter((l) => l.lineId !== lineId);
  } else {
    const line = cart.lines.find((l) => l.lineId === lineId);
    if (line) line.wire.quantity = quantity;
  }
  if (cart.lines.length === 0) write(null);
  else write(cart);
}

export function setChannel(storeKey: string, channel: Channel): void {
  const cart = read(storeKey);
  if (!cart) return;
  cart.channel = channel;
  write(cart);
}

export function clearCart(): void {
  write(null);
}

export function cartCount(cart: Cart | null): number {
  return cart?.lines.reduce((n, l) => n + l.wire.quantity, 0) ?? 0;
}

export function cartSubtotal(cart: Cart | null): number {
  return cart?.lines.reduce((n, l) => n + l.wire.quantity * l.unitPriceInCents, 0) ?? 0;
}
